import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { ensureStickyHeaderVisible, onAnimationEnd } from '@theme/utilities';
import { ADD_TO_CART_FINISHED_EVENT } from '@theme/product-form';

// Longest time the sticky button can show an addition in progress if no answer arrives
const ADDING_STATE_MAX_DURATION = 15000;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {AbortController | null} */
  #stickySheetUiAbort = null;

  /** Evita doble envío al carrito / peticiones en paralelo (Shopify puede responder 429). */
  /** @type {boolean} */
  #applyStickySheetBusy = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    queueMicrotask(() => this.#bindStickySheetDialogEvents());

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickySheetUiAbort?.abort();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  /**
   * Abre el modal de guía de tallas ya renderizado en la PDP (evita duplicar IDs).
   * Invocado con on:click="sticky-add-to-cart/openPdpSizeGuide" para resolver el componente correcto
   * (los hijos del sheet están bajo dialog-component, no bajo sticky-add-to-cart en la delegación).
   */
  openPdpSizeGuide = () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const root = document.querySelector(`dialog-component#nw-size-guide-${sectionId}`);
    const primaryTrigger = root?.querySelector('.nw-size-guide-dialog__trigger');
    if (primaryTrigger instanceof HTMLElement) {
      primaryTrigger.click();
    }
  };

  /**
   * Aplica la talla elegida en el sheet (radios locales) al variant-picker principal y espera al morph.
   * @returns {Promise<void>}
   */
  #applyStickyPreviewSelectionToMainPicker = async () => {
    const sheet = this.querySelector('.sticky-product-size-picker');
    if (!sheet) return;

    const checked = sheet.querySelector('input[type="radio"][data-sticky-preview-radio]:checked');
    const optionValueId =
      checked instanceof HTMLInputElement ? checked.dataset.optionValueId : undefined;
    const productId = this.dataset.productId;
    if (!optionValueId || !productId) return;

    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const input = picker?.querySelector(`input[type="radio"][data-option-value-id="${optionValueId}"]`);

    if (!(input instanceof HTMLInputElement)) return;

    if (input.checked) return;

    input.click();
    await this.#waitForVariantUpdate(productId);
  };

  /**
   * @param {string} productId
   * @returns {Promise<void>}
   */
  #waitForVariantUpdate = (productId) => {
    return new Promise((resolve) => {
      const section = this.closest('.shopify-section');
      if (!section) {
        resolve();
        return;
      }
      const timeoutId = window.setTimeout(() => {
        section.removeEventListener(ThemeEvents.variantUpdate, onUpdate);
        resolve();
      }, 2500);
      const onUpdate = (/** @type {CustomEvent} */ event) => {
        if (event.detail?.data?.productId !== productId) return;
        window.clearTimeout(timeoutId);
        section.removeEventListener(ThemeEvents.variantUpdate, onUpdate);
        resolve();
      };
      section.addEventListener(ThemeEvents.variantUpdate, onUpdate, { once: true });
    });
  };

  /**
   * Sincroniza talla con la PDP, cierra el sheet y añade al carrito dejando que el cart drawer
   * se abra con el comportamiento normal del tema.
   */
  applyStickySheetSelectionAndAddToCart = async () => {
    if (this.#applyStickySheetBusy) return;
    this.#applyStickySheetBusy = true;

    const atcBtn = this.querySelector('[data-sticky-sheet-atc-button]');
    const pickerPanel = this.querySelector('[data-sticky-sheet-panel="picker"]');

    if (atcBtn instanceof HTMLButtonElement) {
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');
    }
    if (pickerPanel instanceof HTMLElement) {
      pickerPanel.setAttribute('aria-busy', 'true');
    }

    try {
      await this.#applyStickyPreviewSelectionToMainPicker();
      await this.closeStickySizeSheet();
      // The sheet is for mobile, where its opener is the visible sticky button: it shows the progress once the sheet
      // has closed.
      const sheetOpener = this.querySelector('dialog-component[data-sticky-size-sheet] > .add-to-cart-button');
      await this.#submitAddToCart({
        triggerButton: sheetOpener instanceof HTMLButtonElement ? sheetOpener : this.refs.addToCartButton,
        suppressCartDrawerAutoOpen: false,
      });
    } finally {
      this.#applyStickySheetBusy = false;

      if (atcBtn instanceof HTMLButtonElement) {
        atcBtn.removeAttribute('aria-busy');
        const stillPicker = pickerPanel instanceof HTMLElement && !pickerPanel.hidden;
        if (stillPicker && this.dataset.variantAvailable === 'true') {
          atcBtn.disabled = false;
        }
      }
      if (pickerPanel instanceof HTMLElement) {
        pickerPanel.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * Cierra solo el bottom sheet de tallas (p. ej. «Seguir comprando»).
   */
  closeStickySizeSheet = async () => {
    const sheet = /** @type {HTMLElement & { closeDialog?: () => Promise<void> }} */ (
      this.querySelector('dialog-component[data-sticky-size-sheet]')
    );
    if (sheet?.closeDialog) await sheet.closeDialog();
  };

  /**
   * Navega al checkout (URL en data-checkout-url del host).
   */
  goToCheckout = () => {
    const url = this.dataset.checkoutUrl?.trim();
    if (url) {
      window.location.assign(url);
    } else {
      window.location.assign('/checkout');
    }
  };

  /**
   * Espera la respuesta del alta desde el formulario de producto (no otros refrescos del carrito).
   * @returns {Promise<'ok' | 'err' | 'timeout'>}
   */
  #waitForStickySheetCartAdd = () => {
    return new Promise((resolve) => {
      let settled = false;
      /** @param {'ok' | 'err' | 'timeout'} result */
      const done = (result) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };
      const productForm = this.#getProductForm();
      /** Escuchar en el formulario del producto evita que otro cart:update global resuelva antes que el CartAddEvent correcto. */
      const listenTarget = productForm ?? document;

      const cleanup = () => {
        listenTarget.removeEventListener(ThemeEvents.cartUpdate, onCartUpdate);
        listenTarget.removeEventListener(ThemeEvents.cartError, onCartErr);
        window.clearTimeout(timeoutId);
      };

      /** @param {Event} event */
      const onCartUpdate = (event) => {
        const e = /** @type {Event & { detail?: { data?: Record<string, unknown> } }} */ (event);
        const detail = e.detail;
        if (detail?.data?.source !== 'product-form-component') return;
        if (detail?.data?.didError) return;
        const pid = this.dataset.productId;
        if (pid && detail?.data?.productId != null && String(detail.data.productId) !== String(pid)) return;
        done('ok');
      };

      const onCartErr = () => done('err');

      listenTarget.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
      listenTarget.addEventListener(ThemeEvents.cartError, onCartErr);

      const timeoutId = window.setTimeout(() => done('timeout'), 12000);
    });
  };

  #bindStickySheetDialogEvents = () => {
    this.#stickySheetUiAbort?.abort();
    this.#stickySheetUiAbort = new AbortController();
    const sheet = this.querySelector('dialog-component[data-sticky-size-sheet]');
    const { signal } = this.#stickySheetUiAbort;
    // Solo al cerrar: resetear a picker. Escuchar dialog:open aquí vaciaba el estado «añadido»
    // si dialog:open se disparaba de nuevo (p. ej. tras morph) con el sheet ya abierto.
    sheet?.addEventListener('dialog:close', this.#resetStickySheetPanels, { signal });
    sheet?.addEventListener('change', this.#syncStickySheetActionState, { signal });

    queueMicrotask(() => this.#syncStickySheetActionState());
  };

  #syncStickySheetActionState = () => {
    const atcBtn = this.querySelector('[data-sticky-sheet-atc-button]');
    if (!(atcBtn instanceof HTMLButtonElement)) return;

    const checkedRadio = this.querySelector('input[type="radio"][data-sticky-preview-radio]:checked');
    const hasEnabledSelection = checkedRadio instanceof HTMLInputElement && checkedRadio.disabled === false;
    const labelNode = atcBtn.querySelector('.font-ocpickllle-bold');

    if (hasEnabledSelection) {
      atcBtn.disabled = false;
      if (labelNode instanceof HTMLElement && this.dataset.addToCartLabel) {
        labelNode.textContent = this.dataset.addToCartLabel;
      }
      return;
    }

    if (this.dataset.requireExplicitSizeSelection === 'true') {
      atcBtn.disabled = true;
      if (labelNode instanceof HTMLElement && this.dataset.selectSizeLabel) {
        labelNode.textContent = this.dataset.selectSizeLabel;
      }
    }
  };

  #resetStickySheetPanels = () => {
    const picker = this.querySelector('[data-sticky-sheet-panel="picker"]');
    const added = this.querySelector('[data-sticky-sheet-panel="added"]');
    if (!(picker instanceof HTMLElement) || !(added instanceof HTMLElement)) return;

    picker.hidden = false;
    added.hidden = true;
    this.dataset.stickySheetView = 'picker';
  };

  /**
   * El panel «añadido» estaba visible (sheet abierto tras alta correcta).
   */
  #isStickySheetAddedPanelVisible = () => {
    const sheet = this.querySelector('dialog-component[data-sticky-size-sheet]');
    const dialogEl = sheet?.querySelector('dialog');
    const added = this.querySelector('[data-sticky-sheet-panel="added"]');
    return (
      dialogEl instanceof HTMLDialogElement &&
      dialogEl.open &&
      added instanceof HTMLElement &&
      added.hidden === false
    );
  };

  #showStickySheetAddedState = () => {
    const sheet = this.querySelector('dialog-component[data-sticky-size-sheet]');
    const dialog = sheet?.querySelector('dialog');
    if (!dialog?.open) return;

    const picker = this.querySelector('[data-sticky-sheet-panel="picker"]');
    const added = this.querySelector('[data-sticky-sheet-panel="added"]');
    if (!(picker instanceof HTMLElement) || !(added instanceof HTMLElement)) return;

    picker.hidden = true;
    added.hidden = false;
    this.dataset.stickySheetView = 'added';
  };

  handleAddToCartClick = async (optionsOrEvent, maybeEvent) => {
    const options =
      maybeEvent && typeof optionsOrEvent === 'object' && optionsOrEvent !== null ? optionsOrEvent : {};
    const event = maybeEvent ?? optionsOrEvent;
    const triggerButton =
      event?.target instanceof HTMLButtonElement ? event.target : this.refs.addToCartButton;

    await this.#submitAddToCart({
      triggerButton,
      suppressCartDrawerAutoOpen: options.openDrawer !== true,
    });
  };

  /**
   * Dispara el add-to-cart principal desde la sticky bar.
   * @param {{ triggerButton?: HTMLButtonElement | null, suppressCartDrawerAutoOpen?: boolean }} [options]
   */
  #submitAddToCart = async ({ triggerButton, suppressCartDrawerAutoOpen = true } = {}) => {
    if (!this.#targetAddToCartButton) return;

    const productForm = /** @type {{ hasPendingSizeSelection?: boolean, openSizeSelectionDialog?: Function } | null } */ (this.#getProductForm());
    if (productForm?.hasPendingSizeSelection && typeof productForm.openSizeSelectionDialog === 'function') {
      productForm.openSizeSelectionDialog();
      return;
    }

    if (suppressCartDrawerAutoOpen) {
      this.#targetAddToCartButton.dataset.puppet = 'true';
    } else {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (this.#targetAddToCartButton.disabled) return;

    // The sticky button shows the progress until the main form says the addition ended (spinner, see base.css), and
    // only then the "added" animation.
    const clickedButton = triggerButton ?? this.refs.addToCartButton;
    const finished = this.#waitForAddToCartFinished();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    clickedButton?.removeAttribute('data-added');
    clickedButton?.setAttribute('aria-busy', 'true');

    this.#targetAddToCartButton.click();
    const added = await finished;
    clickedButton?.removeAttribute('aria-busy');

    // Buttons without the "added" markup (the sheet opener) only show the progress.
    if (!clickedButton || !added || !clickedButton.querySelector('.add-to-cart__added')) return;
    if (clickedButton.dataset.added !== 'true') {
      clickedButton.dataset.added = 'true';
    }

    if (this.dataset.addToCartAnimation !== 'true') {
      this.#resetTimeout = setTimeout(() => {
        clickedButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    ensureStickyHeaderVisible();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([clickedButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      clickedButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const restoreAddedPanelAfterMorph = this.#isStickySheetAddedPanelVisible();

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    this.#bindStickySheetDialogEvents();

    if (restoreAddedPanelAfterMorph) {
      queueMicrotask(() => this.#showStickySheetAddedState());
    }
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  /**
   * Resolves when the main product form ends the addition the sticky bar triggered.
   * @returns {Promise<boolean>} Whether the product was added (false after a while without an answer)
   */
  #waitForAddToCartFinished() {
    const productForm = this.#getProductForm();
    if (!productForm) return Promise.resolve(false);

    return new Promise((resolve) => {
      /** @param {Event} event */
      const onFinished = (event) => {
        clearTimeout(timeoutId);
        resolve(Boolean(/** @type {CustomEvent} */ (event).detail?.added));
      };
      const timeoutId = setTimeout(() => {
        productForm.removeEventListener(ADD_TO_CART_FINISHED_EVENT, onFinished);
        resolve(false);
      }, ADDING_STATE_MAX_DURATION);

      productForm.addEventListener(ADD_TO_CART_FINISHED_EVENT, onFinished, { once: true });
    });
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
